import { Dialog } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { InventoryAdjustmentLaunchStep } from "./InventoryAdjustmentLaunchStep"
import { InventoryAdjustmentQuantityStep } from "./InventoryAdjustmentQuantityStep"
import { buildInventoryAdjustmentWarehouseOptions } from "./utils"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { InventoryAdjustmentType, InventoryByProductItem } from "@/graphql/codegen/graphql"

enum DialogStep {
  Launch = "launch",
  AdjustQuantity = "adjust-quantity",
}

interface InventoryAdjustmentDialogProps {
  productInventory: InventoryByProductItem
  onClose: () => void
}

export const InventoryAdjustmentDialog: FC<InventoryAdjustmentDialogProps> = ({ productInventory, onClose }) => {
  const [step, setStep] = useState<DialogStep>(DialogStep.Launch)
  const [selectedWarehouseAreaId, setSelectedWarehouseAreaId] = useState("")
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const warehouseOptions = useMemo(
    () => buildInventoryAdjustmentWarehouseOptions(productInventory.warehouses),
    [productInventory.warehouses]
  )

  const handleContinue = (warehouseAreaId: string, _adjustmentType: InventoryAdjustmentType) => {
    setSelectedWarehouseAreaId(warehouseAreaId)
    // TODO: Handle other adjustment types when implemented
    setStep(DialogStep.AdjustQuantity)
  }

  const handleClose = () => {
    if (step === DialogStep.AdjustQuantity) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth={step === DialogStep.AdjustQuantity ? "xl" : "sm"}
        fullWidth
        PaperProps={
          step === DialogStep.AdjustQuantity ? { sx: { maxWidth: "min(1600px, 95vw)" } } : { sx: { minWidth: 560 } }
        }
        data-testid='inventory-adjustment.dialog'
      >
        <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleClose} />
        {step === DialogStep.Launch && (
          <InventoryAdjustmentLaunchStep
            product={productInventory.product}
            warehouseOptions={warehouseOptions}
            onCancel={onClose}
            onContinue={handleContinue}
          />
        )}
        {step === DialogStep.AdjustQuantity && (
          <InventoryAdjustmentQuantityStep
            product={productInventory.product}
            defaultWarehouseAreaId={selectedWarehouseAreaId}
            warehouseInventories={productInventory.warehouses}
            warehouseOptions={warehouseOptions}
            onCancel={handleClose}
            onSuccess={onClose}
          />
        )}
      </Dialog>

      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      )}
    </>
  )
}
